// frontend/src/components/profile/FleetManager.js
import React, { useState, useEffect } from 'react';
import { fleetsApi } from '../../api/fleets';
import { FaTruck, FaPlus, FaTrash, FaArrowLeft, FaCar, FaTimes, FaSpinner, FaInfoCircle } from 'react-icons/fa';

function FleetManager() {
  const [fleets, setFleets] = useState([]);
  const [selectedFleet, setSelectedFleet] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create Fleet Modal State
  const [isCreatingFleet, setIsCreatingFleet] = useState(false);
  const [newFleetData, setNewFleetData] = useState({ 
    name: '', 
    description: '', 
    contact_phone: '', 
    contact_email: '' 
  });

  // Add Vehicle Modal State
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  const [newVehicleData, setNewVehicleData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    plate_number: '',
    current_mileage: ''
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFleets();
  }, []);

  const fetchFleets = async () => {
    try {
      const data = await fleetsApi.getMyFleets();
      setFleets(data);
    } catch (err) { 
      console.error("Error fetching fleets:", err); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleViewFleet = async (fleet) => {
    setSelectedFleet(fleet);
    setLoading(true);
    try {
      const data = await fleetsApi.getFleetVehicles(fleet.id);
      setVehicles(data);
    } catch (err) { 
      console.error("Error fetching fleet vehicles:", err); 
    } finally { 
      setLoading(false); 
    }
  };

  // --- FLEET ACTIONS ---

  const handleCreateFleet = async (e) => {
    e.preventDefault();
    if (!newFleetData.name.trim()) return alert("Fleet name is required.");

    setSubmitting(true);
    try {
      await fleetsApi.createFleet(newFleetData);
      await fetchFleets();
      setIsCreatingFleet(false);
      setNewFleetData({ name: '', description: '', contact_phone: '', contact_email: '' });
    } catch (err) {
      alert("Failed to create fleet.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFleet = async (e, fleetId) => {
    e.stopPropagation(); // Prevent opening the fleet details
    if (!window.confirm("Are you sure you want to delete this fleet? All vehicles in it will be removed.")) return;

    try {
      await fleetsApi.deleteFleet(fleetId);
      setFleets(prev => prev.filter(f => f.id !== fleetId));
    } catch (err) {
      alert("Failed to delete fleet.");
    }
  };

  // --- VEHICLE ACTIONS ---

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...newVehicleData,
        year: parseInt(newVehicleData.year),
        current_mileage: parseInt(newVehicleData.current_mileage) || 0
      };
      
      await fleetsApi.addVehicleToFleet(selectedFleet.id, payload);
      
      // Refresh vehicle list
      const updatedVehicles = await fleetsApi.getFleetVehicles(selectedFleet.id);
      setVehicles(updatedVehicles);
      
      setIsAddingVehicle(false);
      setNewVehicleData({ make: '', model: '', year: new Date().getFullYear(), color: '', plate_number: '', current_mileage: '' });
    } catch (err) {
      alert("Failed to add vehicle.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveVehicle = async (vehicleId) => {
    if (!window.confirm("Remove this vehicle from the fleet?")) return;
    try {
      await fleetsApi.removeVehicleFromFleet(selectedFleet.id, vehicleId);
      setVehicles(prev => prev.filter(v => v.id !== vehicleId));
    } catch (err) {
      alert("Failed to remove vehicle.");
    }
  };

  if (loading && !selectedFleet) return <div className="text-center py-10 text-gray-500">Loading Fleets...</div>;

  return (
    <div className="relative">
      {!selectedFleet ? (
        // --- LIST VIEW ---
        <>
          {/* HEADER: Stack on mobile, Row on desktop */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <FaTruck className="text-primary" /> Fleet Management
            </h2>
            <button 
              onClick={() => setIsCreatingFleet(true)}
              className="bg-primary hover:bg-primary-dark text-white px-5 py-2 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition-colors shadow-sm w-full sm:w-auto"
            >
              <FaPlus size={12} /> New Fleet
            </button>
          </div>

          {/* --- TOOLKIT --- */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm flex items-start gap-3">
              <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
              <div>
                  <h4 className="font-bold text-blue-800 text-sm">Organize Your Business</h4>
                  <p className="text-sm text-blue-700">
                      Create fleets to group your vehicles (e.g., "Lagos Delivery Vans", "Executive Cars"). This helps in tracking maintenance schedules and managing assets efficiently.
                  </p>
              </div>
          </div>

          {fleets.length === 0 ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-16 text-center bg-gray-50/50">
              <p className="text-gray-800 font-medium mb-3 text-lg">No fleets created yet.</p>
              <button onClick={() => setIsCreatingFleet(true)} className="text-primary hover:underline">Create your first fleet</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fleets.map(f => (
                <div 
                  key={f.id} 
                  onClick={() => handleViewFleet(f)} 
                  className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md cursor-pointer transition-all hover:border-primary group relative"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800 text-lg group-hover:text-primary transition-colors">{f.name}</h3>
                    <span className="text-xs bg-blue-50 text-primary px-2 py-1 rounded font-bold">{f.vehicle_count} Vehicles</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{f.description || 'No description provided.'}</p>
                  
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                    <button 
                        onClick={(e) => handleDeleteFleet(e, f.id)}
                        className="text-red-400 hover:text-red-600 text-sm flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                    >
                        <FaTrash size={12} /> Delete
                    </button>
                    <span className="text-xs text-gray-400 flex items-center gap-1 group-hover:text-primary">
                        Manage <FaArrowLeft className="rotate-180" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        // --- DETAIL VIEW ---
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <button onClick={() => setSelectedFleet(null)} className="text-gray-500 hover:text-primary flex items-center gap-2 font-medium text-sm">
                <FaArrowLeft /> Back to Fleets
            </button>
            <button 
                onClick={() => setIsAddingVehicle(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-bold flex items-center justify-center gap-2 shadow-sm w-full sm:w-auto"
            >
                <FaPlus size={12} /> Add Vehicle
            </button>
          </div>
          
          <div className="bg-blue-50 p-6 rounded-lg mb-8 border border-blue-100">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedFleet.name}</h2>
                    <p className="text-gray-600">{selectedFleet.description}</p>
                </div>
                <div className="text-left md:text-right text-sm text-gray-500 bg-white p-3 rounded border border-blue-100 w-full md:w-auto">
                    {selectedFleet.contact_phone && <p><strong>Phone:</strong> {selectedFleet.contact_phone}</p>}
                    {/* Added break-all to prevent email overflow */}
                    {selectedFleet.contact_email && <p className="break-all"><strong>Email:</strong> {selectedFleet.contact_email}</p>}
                </div>
            </div>
          </div>

          <div className="space-y-4">
            {vehicles.length === 0 ? (
               <div className="text-center py-16 bg-gray-50 rounded border border-dashed border-gray-300">
                   <FaCar className="mx-auto text-gray-300 text-4xl mb-3" />
                   <p className="text-gray-500 italic">No vehicles in this fleet yet.</p>
                   <button onClick={() => setIsAddingVehicle(true)} className="text-primary hover:underline mt-2">Add a vehicle</button>
               </div>
            ) : (
              vehicles.map(v => (
                <div key={v.id} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-4 w-full sm:w-auto">
                    <div className="bg-gray-100 p-3 rounded-full text-gray-500">
                      <FaCar size={20} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-lg">{v.year} {v.make} {v.model}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-1">
                          <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200 font-mono text-gray-700">{v.plate_number || 'NO PLATE'}</span>
                          <span>{v.color}</span>
                          <span>{v.current_mileage.toLocaleString()} km</span>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleRemoveVehicle(v.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors flex items-center gap-2 text-sm font-medium w-full sm:w-auto justify-center"
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* --- CREATE FLEET MODAL --- */}
      {isCreatingFleet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden p-6 animate-fadeInUp">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-lg text-gray-900">Create New Fleet</h3>
                <button onClick={() => setIsCreatingFleet(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            
            <form onSubmit={handleCreateFleet} className="space-y-4">
              <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Fleet Name *</label>
                  <input 
                    type="text" required
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-primary outline-none"
                    value={newFleetData.name}
                    onChange={(e) => setNewFleetData({...newFleetData, name: e.target.value})}
                  />
              </div>
              <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
                  <input 
                    type="text"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-primary outline-none"
                    value={newFleetData.description}
                    onChange={(e) => setNewFleetData({...newFleetData, description: e.target.value})}
                  />
              </div>
              <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Phone</label>
                      <input 
                        type="tel"
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-primary outline-none"
                        value={newFleetData.contact_phone}
                        onChange={(e) => setNewFleetData({...newFleetData, contact_phone: e.target.value})}
                      />
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                      <input 
                        type="email"
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-primary outline-none"
                        value={newFleetData.contact_email}
                        onChange={(e) => setNewFleetData({...newFleetData, contact_email: e.target.value})}
                      />
                  </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setIsCreatingFleet(false)} className="text-gray-600 px-4 py-2">Cancel</button>
                <button type="submit" disabled={submitting} className="bg-primary text-white px-6 py-2 rounded font-bold hover:bg-primary-dark">
                  {submitting ? <FaSpinner className="animate-spin" /> : 'Create Fleet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- ADD VEHICLE MODAL --- */}
      {isAddingVehicle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden p-6 animate-fadeInUp">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-lg text-gray-900">Add Vehicle to {selectedFleet?.name}</h3>
                <button onClick={() => setIsAddingVehicle(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            
            <form onSubmit={handleAddVehicle} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Make *</label>
                      <input required className="w-full p-2 border rounded" value={newVehicleData.make} onChange={(e) => setNewVehicleData({...newVehicleData, make: e.target.value})} />
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Model *</label>
                      <input required className="w-full p-2 border rounded" value={newVehicleData.model} onChange={(e) => setNewVehicleData({...newVehicleData, model: e.target.value})} />
                  </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Year *</label>
                      <input type="number" required className="w-full p-2 border rounded" value={newVehicleData.year} onChange={(e) => setNewVehicleData({...newVehicleData, year: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Color</label>
                      <input className="w-full p-2 border rounded" value={newVehicleData.color} onChange={(e) => setNewVehicleData({...newVehicleData, color: e.target.value})} />
                  </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Plate Number</label>
                      <input className="w-full p-2 border rounded uppercase" placeholder="ABC-123DE" value={newVehicleData.plate_number} onChange={(e) => setNewVehicleData({...newVehicleData, plate_number: e.target.value})} />
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mileage (km)</label>
                      <input type="number" className="w-full p-2 border rounded" value={newVehicleData.current_mileage} onChange={(e) => setNewVehicleData({...newVehicleData, current_mileage: e.target.value})} />
                  </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setIsAddingVehicle(false)} className="text-gray-600 px-4 py-2">Cancel</button>
                <button type="submit" disabled={submitting} className="bg-green-600 text-white px-6 py-2 rounded font-bold hover:bg-green-700">
                  {submitting ? <FaSpinner className="animate-spin" /> : 'Add Vehicle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default FleetManager;